import styled from 'styled-components'
import { useQuery } from 'react-query'
import { useContext, useMemo } from 'react'

import { api } from '@h2/api'
import { Globals } from '@h2/globals'
import { features } from '@h2/components/media'
import { SLB10S, SLB90T } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Skeleton } from '@h2/components/loading/skeleton'
import { calculateCurrentGradeLevelId } from '@h2/common/actions/gradeLevels'
import { gradesToNames } from '@h2/features/planning/components/earlyImages'

import { LessonImage } from './lessonImage'
import { LessonInfo } from './lessonInfo'
import { LessonLinks } from './lessonLinks'
import { LessonMessageContainer } from './common/lessonMessageContainer'
import { ChooseGrade } from './chooseGrade'
import { CompletedLessons } from './completedLesson'

const ResponsiveSkeleton = styled(Skeleton)`
  height: 820px;

  @media (${features.minWidth.laptop}) {
    height: 300px;
  }
`
const Wrap = styled(Flex)`
  flex-direction: column;
  gap: 50px;
  padding: 20px;
  margin-bottom: 20px;
  border: ${SLB10S} solid 4px;
  border-radius: 10px;
  background-color: ${SLB90T};

  > img {
    align-self: start;
    height: auto;
    width: auto;
    outline: 4px solid ${SLB90T};
  }

  @media (${features.minWidth.desktop}) {
    flex-direction: row;
    padding: 20px;

    > img {
      width: 335px;
    }
  }
`
const InnerWrap = styled(Flex)`
  width: 100%;
`
const Lessons = ({ mostRecentActivity, isFetchingMostRecentActivity }) => {
  const { userProfile: user } = useContext(Globals)
  // Determine lesson most recently worked on that is still in progress
  const mostRecentLessonsIncomplete = mostRecentActivity?.filter(
    lesson => lesson.percentComplete < 100
  )
  const nextAssignedLesson = mostRecentActivity?.find(
    lesson => lesson.percentComplete < 100 && lesson.isAssigned === true
  )
  // Assigned Lesson or User Initiated Lesson Detail
  const assignedOrUserInitiatedLesson =
    mostRecentLessonsIncomplete && mostRecentLessonsIncomplete[0]
  // Determine Student's Grade Level
  const { data: gradeLevels, isFetching: isFetchingGadeLevels } = useQuery(
    'plans-grades',
    () => {
      return api.fetch('/plans/grades')
    }
  )
  const currentGradeId = useMemo(() => {
    if (gradeLevels) {
      if (user.primaryType === 'student' && user.graduationYear) {
        return calculateCurrentGradeLevelId(user.graduationYear, gradeLevels)
      }

      if (user.primaryType === 'student' && !user.graduationYear) {
        return null
      }

      if (user.primaryType !== 'student') {
        /**
         * ideally assign appropriate grade for the currentGradeId for educators, e.g. 5th grade teacher sees 5th grade lessons. Assigning kindergarten grade for all staff
         * selecting graduation year is not a required field for students, so need to handle accordingly. Assigning undefined for currentGradeId for students who did not select graduation year
         */
        return gradeLevels?.grades[0]
      }

      return null
    }
  }, [gradeLevels, user])
  // List of All Lessons Within a Grade Level And Status of Lesson Submissions
  const { data: gradeLessons, isFetching: isFetchingGradeLessons } = useQuery(
    ['grade-lessons', currentGradeId],
    () => {
      return api.fetch(`/plans/grades/${currentGradeId}`)
    },
    {
      enabled:
        currentGradeId >= 0 &&
        currentGradeId !== null &&
        !isFetchingGadeLevels &&
        !nextAssignedLesson
    }
  )
  const completedGradeLesson = gradeLessons?.progress?.plansInProgress.filter(
    lesson => lesson.dateSubmitted !== null
  )
  const nextGradeLesson = gradeLessons?.plans.filter(
    gradeLesson =>
      !completedGradeLesson?.some(
        completedLesson => completedLesson.parent === gradeLesson.id
      )
  )
  const gradeLessonDetail = nextGradeLesson?.find(next => !!next.id)
  const currentGradeLesson = gradeLessons?.progress?.plansInProgress.find(
    lesson => lesson.dateSubmitted === null
  )
  const currentGradeLessonId = currentGradeLesson?.id
  // Current Grade Lesson Progress Bar Value
  const { data: gradeLessonProgressBar, isFetching: isFetchingGradeLessonProgressBar } =
    useQuery(
      ['grade-lesson-detail', currentGradeLessonId],
      () => {
        return api.fetch(`/management/assignments/${currentGradeLessonId}`)
      },
      {
        enabled: !!currentGradeLessonId && !nextAssignedLesson
      }
    )
  const gradeLessonPercentComplete = currentGradeLesson
    ? parseInt(gradeLessonProgressBar?.percentComplete) || 0
    : 0
  // Current Grade Level Image
  const gradeLevel = currentGradeId && gradesToNames[currentGradeId]
  // Capturing edge cases and other workflows
  const assignedLessons = mostRecentActivity?.some(lesson => lesson.isAssigned === true)
  const currentUserInitiatedLesson = mostRecentActivity?.find(
    lesson => lesson.percentComplete < 100 && lesson.isAssigned === false
  )
  const studentWithoutAssignedLessonsOrGrade =
    user.primaryType === 'student' &&
    user.graduationYear === null &&
    !nextAssignedLesson &&
    !assignedOrUserInitiatedLesson // includes students who chose grade level above 5th grade, see gradeLevels.js
  const studentFinishedAssignedLessonsMissingGraduationInfo =
    user.primaryType === 'student' &&
    user.graduationYear === null &&
    !nextAssignedLesson &&
    assignedLessons === true
  const studentWithoutAssignedLessonsFinishedGradeLessons =
    user.primaryType === 'student' && !nextAssignedLesson && nextGradeLesson?.length === 0
  const renderStyleForNewLesson =
    assignedOrUserInitiatedLesson?.percentComplete === 0 ||
    (!currentUserInitiatedLesson &&
      !nextAssignedLesson &&
      (gradeLessonPercentComplete === 0 || completedGradeLesson?.length === 0))
  const lessonToDisplay = {
    id: '',
    name: '',
    description: '',
    percentComplete: '',
    imageAlias: ''
  }

  if (assignedOrUserInitiatedLesson) {
    lessonToDisplay.id = assignedOrUserInitiatedLesson?.id
    lessonToDisplay.name = assignedOrUserInitiatedLesson?.name
    lessonToDisplay.description = assignedOrUserInitiatedLesson?.description
    lessonToDisplay.percentComplete = assignedOrUserInitiatedLesson?.percentComplete
    lessonToDisplay.imageAlias = assignedOrUserInitiatedLesson?.alias
  }

  if (!assignedOrUserInitiatedLesson && nextGradeLesson) {
    lessonToDisplay.id = gradeLessonDetail?.id
    lessonToDisplay.name = gradeLessonDetail?.name
    lessonToDisplay.description = gradeLessonDetail?.description
    lessonToDisplay.percentComplete = gradeLessonPercentComplete
    lessonToDisplay.imageAlias = gradeLevel?.image
  }

  // Decision Tree for Rendering UI
  const renderLessons = () => {
    if (studentWithoutAssignedLessonsFinishedGradeLessons) {
      return (
        <LessonMessageContainer>
          <CompletedLessons />
        </LessonMessageContainer>
      )
    }

    if (
      studentWithoutAssignedLessonsOrGrade ||
      studentFinishedAssignedLessonsMissingGraduationInfo
    ) {
      return (
        <LessonMessageContainer>
          <ChooseGrade />
        </LessonMessageContainer>
      )
    }

    return (
      <>
        <LessonImage imageAlias={lessonToDisplay.imageAlias} />
        <InnerWrap direction="column">
          <LessonInfo
            lessonToDisplay={lessonToDisplay}
            renderStyleForNewLesson={renderStyleForNewLesson}
          />
          <Flex
            wrap="wrap"
            justifyContent={renderStyleForNewLesson ? 'end' : 'space-between'}
            gap="10px"
            margin={renderStyleForNewLesson ? '0px' : '50px 0px 0px 0px'}>
            <LessonLinks
              lessonToDisplay={lessonToDisplay}
              renderStyleForNewLesson={renderStyleForNewLesson}
              unfinishedAssignedOrUserInitiatedLessons={assignedOrUserInitiatedLesson}
              currentGradeId={currentGradeId}
            />
          </Flex>
        </InnerWrap>
      </>
    )
  }

  if (
    isFetchingMostRecentActivity ||
    isFetchingGadeLevels ||
    isFetchingGradeLessons ||
    isFetchingGradeLessonProgressBar
  ) {
    return <ResponsiveSkeleton display="block" radius="10px" margin="0px 0px 20px 0px" />
  }

  return <Wrap>{renderLessons()}</Wrap>
}

export { Lessons }
